/* ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
 * W140 (S4) — EVERY CHOICE A COACH MAKES ABOUT HOW THE APP LOOKS IS REMEMBERED, PER COACH, ON THIS BROWSER, AND APPLIES
 * EVERYWHERE. His words (2026-10-02 ~13:20): *"make sure the last options selected in the extended/minimized and scroll
 * settings too and all, like dark, light or auto versions are stored and used next time as the coach stored them, even
 * for other workouts, clients and all."*
 *
 * ONE mechanism, so the choices cannot drift into ten private conventions (they were: seven flat keys, two spellings —
 * `ht-week-mode` and `progress.range` — and none of them knew whose choice it was):
 *  • TYPED KEYS (`ViewPrefKey`) — a caller cannot ask for a key nobody writes.
 *  • ONE BLOB PER COACH, `ht-view-prefs:<coach id>` = `{"v":1,"prefs":{…}}`, so two coaches on one browser never inherit
 *    each other's choices. The coach is read SYNCHRONOUSLY out of the Supabase session the client already keeps in the
 *    same store (`AUTH_STORAGE_KEY`, which `supabase.ts` passes as its own `storageKey`, so the two cannot disagree) —
 *    synchronous because the theme must be stamped BEFORE the first paint, when no React state exists yet. Signed out
 *    (or the session unreadable) → the `anon` blob.
 *  • A VERSION FIELD — a blob of another version is ignored, never half-read.
 *  • EVERY READ AND WRITE IS try/caught: a private window, a blocked store or a thumbnail capture renders the page with
 *    the defaults; a refused write leaves the URL (where a view has one) as the only memory, which still works.
 *  • THE OLD FLAT KEYS ARE READ ONCE AS A FALLBACK (`LEGACY_KEY`), never written again — a coach's choice made before W140
 *    survives the change. ⚠️ The flat keys belonged to the BROWSER, not a coach, so until a coach writes their own value a
 *    second coach on the same browser sees the pre-W140 choice once. Stated, accepted: it is what they saw yesterday.
 *  • THE URL STILL STATES A VIEW WHEN PRESENT (a pasted link opens as sent) — that precedence lives in each view's own
 *    rule (`weekModeFrom`, `spanFromQuery`, `initialView`), which takes the stored value as its SECOND argument. Changing a
 *    control writes both.
 * ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════ */

export type ViewPrefKey =
  | "theme"            // Light · Dark · Auto
  | "sidebarLocked"    // W140 S2: "1" = the labelled sidebar stays; anything else = the icon rail (S3's default)
  | "weekMode"         // Minimized · Extended — the client Week AND the Program board, one choice
  | "weekScroll"       // Scroll: Each day · Together — same two surfaces
  | "weekNotes"        // Coach notes: Show · Hide — the client Week's extended columns (his ask, 2026-10-15)
  | "weekSpan"         // 1 · 2 · 4 Week — the client Week and the Tasks calendar
  | "weekLibrary"      // week editor stage 2: the library panel beside the client's Extended week, open ("1") or not
  | "shelfExercises"   // the Library page's List · Grid
  | "shelfBuilderPane" // the builder's library pane's List · Grid (its own answer, `shelfView.ts`)
  | "builderOutline"   // the builder's Arrange outline, open or closed
  | "progressRange"    // the progress chart's range
  | "progressField";   // the progress chart's field

export const VIEW_PREF_KEYS: readonly ViewPrefKey[] = [
  "theme", "sidebarLocked", "weekMode", "weekScroll", "weekNotes", "weekSpan", "weekLibrary", "shelfExercises", "shelfBuilderPane", "builderOutline",
  "progressRange", "progressField",
];

/** The pre-W140 flat key each choice used to live under — read as a fallback, never written. */
export const LEGACY_KEY: Readonly<Partial<Record<ViewPrefKey, string>>> = {
  theme: "ht-theme",
  weekMode: "ht-week-mode",
  weekScroll: "ht-week-scroll",
  shelfExercises: "ht-exercises-view",
  shelfBuilderPane: "ht-builder-pane-view",
  builderOutline: "ht.builder.outline",
  progressRange: "progress.range",
  progressField: "progress.field",
};

export const VIEW_PREFS_VERSION = 1;
const PREFIX = "ht-view-prefs:";

/** Where the Supabase client keeps the session — `supabase.ts` passes THIS as `storageKey` (the library's own default
 *  spelling, `sb-<project ref>-auth-token`, stated once). */
export const AUTH_STORAGE_KEY = "sb-xtjbfaukkcfbdaadlldl-auth-token";

export type PrefStore = Pick<Storage, "getItem" | "setItem">;

/** The browser's store, or undefined where reaching it throws (a sandboxed frame, blocked site data). */
export function browserStore(): PrefStore | undefined {
  try { return globalThis.localStorage ?? undefined; } catch { return undefined; }
}

/** The signed-in coach's id, read out of the session the auth client keeps, or null. Never throws. */
export function coachIdFrom(store: Pick<Storage, "getItem"> | undefined): string | null {
  try {
    const raw = store?.getItem(AUTH_STORAGE_KEY);
    if (raw == null) return null;
    const id: unknown = (JSON.parse(raw) as { user?: { id?: unknown } } | null)?.user?.id;
    return typeof id === "string" && id.length > 0 ? id : null;
  } catch { return null; }
}

/** The blob's key for a coach (`anon` when nobody is signed in). */
export function prefsKeyFor(coachId: string | null): string {
  return PREFIX + (coachId ?? "anon");
}

type Blob = { v: number; prefs: Partial<Record<ViewPrefKey, string>> };

function readBlob(store: Pick<Storage, "getItem">, key: string): Partial<Record<ViewPrefKey, string>> {
  try {
    const raw = store.getItem(key);
    if (raw == null) return {};
    const parsed = JSON.parse(raw) as Partial<Blob> | null;
    if (parsed?.v !== VIEW_PREFS_VERSION || typeof parsed.prefs !== "object" || parsed.prefs === null) return {};
    return parsed.prefs;
  } catch { return {}; }
}

/** The coach's stored value for `key`, else the pre-W140 flat key's, else null. Never throws. */
export function readPref(key: ViewPrefKey, store: Pick<Storage, "getItem"> | undefined = browserStore(),
                         coachId: string | null = coachIdFrom(store)): string | null {
  if (store === undefined) return null;
  const v = readBlob(store, prefsKeyFor(coachId))[key];
  if (typeof v === "string") return v;
  const legacy = LEGACY_KEY[key];
  if (legacy === undefined) return null;
  try { return store.getItem(legacy); } catch { return null; }
}

/** Writes `key` into the coach's blob, keeping every other choice in it. Never throws. */
export function writePref(key: ViewPrefKey, value: string, store: PrefStore | undefined = browserStore(),
                          coachId: string | null = coachIdFrom(store)): void {
  if (store === undefined) return;
  try {
    const k = prefsKeyFor(coachId);
    const prefs = { ...readBlob(store, k), [key]: value };
    store.setItem(k, JSON.stringify({ v: VIEW_PREFS_VERSION, prefs } satisfies Blob));
  } catch { /* remembered for this page only — the control still works */ }
}
